import { useEffect, useRef } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconDownload } from '@components/icons/download';
import { IconFile } from '@components/icons/file';
import { IconTrash } from '@components/icons/trash';
import { IconUpload } from '@components/icons/upload';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { abbreviateDate } from '@utils/date';

import { Loading } from 'components/Loading';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

export const ApplicationFiles = observer(({ application }) => {
  const { t, lang } = useStore();
  const fileRef = useRef(null);
  const { hasRolePermission } = usePermissions();
  const { showModal, showFullscreenModal } = useModal();

  useEffect(() => {
    application.setUserRead('files');
  }, [application]);

  function uploadClick() {
    fileRef.current.click();
  }

  function onChange(e) {
    const file = e.target.files.length > 0 ? e.target.files[0] : null;
    const formData = new FormData();
    formData.append('attachment', file);
    application.addAttachment(formData);
  }

  function removeAttachment(attachment) {
    showModal('COMMON_REMOVE_CONFIRM', {
      _container: {
        autoWidth: true,
      },
      data: { id: attachment, confirmText: t('modals.removeconfirm.attachment.confirm') },
      confirm: application.removeAttachment,
    });
  }

  const attachments = application.attachments.filter((a) => a.user !== null);

  return (
    <Stack space={4} style={{ maxWidth: 424, marginTop: '1.5rem' }}>
      <Text font="bold14" text={t('application.header.files')} />
      <Stack space={6}>
        {application.uploadingAttachment && <Loading />}
        {attachments.length === 0 && (
          <Card variant="gray" space={4}>
            <Stack space={2} align="center" orientation="horizontal">
              <IconFile color="purple60" />
              <Text font="semibold14" color="purple80" text={t('application.files.nofiles')} />
            </Stack>
          </Card>
        )}
        {attachments.map((attachment) => (
          <Stack space={1} key={attachment.id}>
            <FileBox
              key={attachment.id}
              style={{ cursor: 'pointer' }}
              onClick={() =>
                showFullscreenModal('FILE_PREVIEW', {
                  application: application,
                  value: attachment.id,
                  attachments: attachments,
                })
              }
            >
              <Stack space={2} orientation="horizontal" style={{ overflow: 'hidden' }}>
                <IconFile color="blue100" />
                <Text font="regular16" color="inherit" ellipsis text={attachment.fileName} />
              </Stack>
              {hasRolePermission('application:edit-files') && (
                <Stack space={0} orientation="horizontal" className="icon-box">
                  <IconButton
                    variant="ghost-purple"
                    icon={<IconDownload />}
                    tooltip={t('application.files.download')}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.location = attachment.attachmentOriginal;
                    }}
                  />
                  <IconButton
                    variant="ghost-purple"
                    icon={<IconTrash />}
                    tooltip={t('application.files.remove')}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeAttachment(attachment);
                    }}
                  />
                </Stack>
              )}
            </FileBox>

            {attachment.user && (
              <div>
                <Text
                  font="regular14"
                  color="purple100"
                  text={`${t('application.files.attached')} ${attachment.user.name}`}
                />
                {attachment.date && (
                  <Text
                    font="regular12"
                    color="purple60"
                    text={abbreviateDate(attachment.date, lang)}
                  />
                )}
              </div>
            )}
          </Stack>
        ))}
      </Stack>
      <div>
        <input style={{ display: 'none' }} type="file" ref={fileRef} onChange={onChange} />
        <Button
          variant="secondary"
          iconLeft={<IconUpload />}
          onClick={uploadClick}
          text={t('application.files.add')}
        />
      </div>
      <Text font="regular14" color="purple80" text={t('application.files.converttext')} />
    </Stack>
  );
});

const FileBox = styled.div((p) => ({
  backgroundColor: theme.color.white100,
  border: `0.0625rem solid ${theme.color.purple10}`,
  borderRadius: '0.25rem',
  color: theme.color.purple100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '1rem',
  gap: '0.5rem',
  transition: 'all 200ms ease-out',
  minWidth: 0,

  '&:hover': {
    color: p.theme.colors.blue100,
    cursor: 'pointer',
    borderColor: p.theme.colors.purple20,
    boxShadow: p.theme.shadows.low,

    '.icon-box': {
      display: 'flex',
    },
  },

  '.icon-box': {
    display: 'none',

    button: {
      margin: '-0.5rem 0',
    },
  },

  '@media(max-width: 768px)': {
    padding: '0.5rem 0 0.5rem 0.5rem',
    '.icon-box': {
      display: 'flex',
    },
  },
}));
